import { Link } from "react-router-dom";
import { ChefHat, Trees, MessagesSquare, Users } from "lucide-react";
import Reveal from "@/components/Reveal";
import { IMAGES } from "@/lib/site";

const PROGRAMS = [
  {
    icon: ChefHat,
    tag: "Practical skills",
    title: "Group Cooking Classes",
    body: "Hands-on cooking sessions that build practical life skills, healthy habits, and the social confidence that comes from making and sharing a meal together.",
  },
  {
    icon: Trees,
    tag: "Connection & wellbeing",
    title: "Outdoor Recreational Activities",
    body: "Bushwalks, group adventures and structured outdoor activity that regulate the nervous system, build community, and create the kind of memories that anchor change.",
  },
  {
    icon: MessagesSquare,
    tag: "Mental capacity",
    title: "One-on-One Counselling",
    body: "Personalised counselling sessions with an ACA-registered counsellor — a confidential space to work through what's beneath the behaviour and build the skills to navigate it.",
  },
  {
    icon: Users,
    tag: "Peer & shared learning",
    title: "Group Counselling",
    body: "Facilitated group sessions where participants build emotional regulation, communication, and the social capacity to belong — together.",
  },
];

export default function Program() {
  return (
    <div data-testid="page-program">
      <section className="bg-warm-50 pt-24 pb-20 lg:pt-32 lg:pb-24">
        <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-12">
          <p className="text-xs uppercase tracking-[0.25em] text-brand font-semibold mb-6">Our programs</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] text-ink-900 max-w-4xl">
            Four ways we help people build the skills for a better life.
          </h1>
          <p className="mt-7 text-lg lg:text-xl leading-relaxed text-ink-500 max-w-3xl">
            We offer a range of programs — alone or in combination — designed around the skills each
            participant most needs to develop. Programs run from our Glen Waverley centre and across
            outdoor locations around Melbourne.
          </p>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.2em] text-brand font-semibold mb-5">What we offer</p>
            <h2 className="font-serif text-4xl lg:text-5xl tracking-tight leading-tight text-ink-900 max-w-3xl">
              Practical, social, and emotional — together.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {PROGRAMS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.06}>
                <div
                  data-testid={`program-${i}`}
                  className="h-full rounded-2xl bg-warm-50 border border-warm-200/60 p-10 lg:p-12 hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-900/5 transition-all"
                >
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand/10 text-brand mb-6">
                    <p.icon className="w-6 h-6" />
                  </div>
                  <p className="text-xs uppercase tracking-widest text-ink-400 mb-2">{p.tag}</p>
                  <h3 className="font-serif text-2xl lg:text-3xl text-ink-900 mb-3">{p.title}</h3>
                  <p className="text-ink-500 leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-warm-50">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.2em] text-brand font-semibold mb-5">How it works</p>
            <h2 className="font-serif text-4xl lg:text-5xl tracking-tight leading-tight text-ink-900">
              Tailored to the person, not a single timeline.
            </h2>
            <p className="mt-5 text-lg text-ink-500 leading-relaxed">
              Programs can be accessed individually or combined into a holistic support package. Sessions
              are scheduled around each participant's commitments and case-management plan — group programs
              run on regular weekly cycles, with individual counselling available throughout.
            </p>
            <img src={IMAGES.nature} alt="Bushland path" className="mt-10 rounded-2xl w-full h-72 object-cover" />
          </Reveal>
          <div className="lg:col-span-7 lg:pl-8 space-y-5">
            {[
              ["Step 01", "Conversation", "We talk with the referrer and the participant to understand needs, history and goals."],
              ["Step 02", "Match the right programs", "We recommend the program (or combination) that best supports the participant's skill-building."],
              ["Step 03", "Begin & build", "Participation starts. We share regular updates with the referring case manager."],
              ["Step 04", "Ongoing support", "Programs run as long as the person benefits — and we stay involved through transitions."],
            ].map(([s, t, b], i) => (
              <Reveal key={s} delay={i * 0.05}>
                <div
                  data-testid={`how-it-works-${i}`}
                  className="bg-white border border-warm-200/60 rounded-xl p-6 lg:p-8 hover:border-brand transition-colors"
                >
                  <p className="text-xs uppercase tracking-widest text-brand font-semibold mb-2">{s}</p>
                  <h3 className="font-serif text-xl text-ink-900 mb-1">{t}</h3>
                  <p className="text-ink-500 leading-relaxed">{b}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ink-900 text-white py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-12 text-center">
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-tight">
            Ready to discuss a participant?
          </h2>
          <Link
            to="/professionals"
            className="mt-8 inline-flex items-center rounded-full bg-brand-soft text-ink-900 px-7 py-4 text-sm font-semibold hover:bg-white transition-colors"
            data-testid="program-refer-cta"
          >
            Refer a participant
          </Link>
        </div>
      </section>
    </div>
  );
}
